/*
 *---------------------------------------------------
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *---------------------------------------------------
 */
//standard libraries
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useEffect, useState } from "react";
import { Canceler } from "axios";
import { useHistory } from "react-router";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
//custom libraries
import { useCustomerHandler, useOrgHandler } from "./rest";
import { CUSTOMER, CUSTOMER_NEW } from "../constants/routes";
import addressHelper from "../helpers/addressHelper";
import { useStoreSelection } from "./use-store-selection";
import { useMessageValue, useProgressValue } from "../context";
import { Message, MessageType } from "../constants/message";

export const useAddCustomerForm = () => {
  const { getOrgs } = useOrgHandler();
  const { createCustomerRequest } = useCustomerHandler();
  const { selectedStore } = useStoreSelection();
  const CHALLENGE_QUESTION_ANSWER_VALUE = "-";
  const customerInit = {
    logonId: "",
    password: "",
    passwordVerify: "",
    address: {
      firstName: "",
      lastName: "",
      email1: "",
      phone1: "",
      address1: "",
      zipCode: "",
      country: "",
      state: "",
    },
    profileType: "C",
    receiveSmsNotification: "false",
    challengeQuestion: CHALLENGE_QUESTION_ANSWER_VALUE,
    challengeAnswer: CHALLENGE_QUESTION_ANSWER_VALUE,
  };

  const [customer, setCustomer] = useState<any>(customerInit);
  const cancels: any = { cancels: [] };
  const history = useHistory();
  const theme = useTheme();
  const isMd = !useMediaQuery(theme.breakpoints.up("md"));
  const { t } = useTranslation();
  const { setMessage } = useMessageValue();
  const { setInProgress } = useProgressValue();

  const handleChange = (event: any) => {
    const inputName = event.target.name;
    const inputValue = event.target.value;
    if (
      inputName === "logonId" ||
      inputName === "password" ||
      inputName === "passwordVerify"
    ) {
      setCustomer({ ...customer, [inputName]: inputValue });
    } else if (inputName === "parentOrganizationName") {
      setCustomer({ ...customer, [inputName]: inputValue, profileType: "B" });
    } else {
      setCustomer({
        ...customer,
        address: { ...customer.address, [inputName]: inputValue },
      });
    }
  };

  const createCustomer = (param: any) => {
    const [res, canceler] = createCustomerRequest(param);
    cancels.cancels = canceler;
    res
      .then((r: any) => {
        const customerId = r.headers.location.split("/").pop();
        const location = {
          pathname: `${CUSTOMER}/${customerId}`,
          state: {
            from: CUSTOMER_NEW,
            label: "CreateCustomer.CreateAnotherCustomer",
          },
        };
        history.push(location);
      })
      .catch((e) => console.log(e))
      .finally(() => {
        setInProgress(false);
      });
  };

  const clickSubmit = (event: any) => {
    event.preventDefault();
    setInProgress(true);
    if (!customer.parentOrganizationName) {
      createCustomer(customer);
    } else {
      const parameter = { organizationName: customer.parentOrganizationName };
      const [res, canceler] = getOrgs(parameter);
      cancels.cancels = canceler;
      res
        .then((r: any) => {
          if (r.data.count > 0) {
            const organizationId = r.data.items[0].id;
            const newParams = {
              ...customer,
              parentOrganizationId: organizationId,
            };
            createCustomer(newParams);
          } else {
            setInProgress(false);
            const errorMessage: Message = {
              message: t("Error._ERR_CMD_INVALID_PARAM_parentOrganizationName"),
              type: MessageType.ERROR,
            };
            setMessage(errorMessage);
          }
        })
        .catch((e) => {
          setInProgress(false);
          console.log(e);
        });
    }
  };

  const clickBack = (event: any) => {
    history.goBack();
  };

  const canCreate = (): boolean => {
    if (
      customer.logonId.trim() !== "" &&
      customer.password.trim() !== "" &&
      customer.passwordVerify.trim() !== "" &&
      customer.address.lastName.trim() !== "" &&
      customer.address.email1.trim() !== "" &&
      customer.address.address1.trim() !== "" &&
      customer.address.zipCode.trim() !== "" &&
      customer.address.country.trim() !== "" &&
      customer.address.state.trim() !== ""
    ) {
      if (
        selectedStore.type === "BMH" &&
        (!customer.parentOrganizationName ||
          (customer.parentOrganizationName &&
            customer.parentOrganizationName.trim() === ""))
      ) {
        return false;
      }
      return (
        addressHelper.validateEmail(customer.address.email1) &&
        addressHelper.validatePhoneNumber(customer.address.phone1) &&
        validatePassword(customer.password, customer.passwordVerify)
      );
    }
    return false;
  };

  const validatePassword = (password1: string, password2: string): boolean => {
    if (password1 !== password2) {
      return false;
    } else {
      return true;
    }
  };

  useEffect(() => {
    return () => {
      cancels.cancels.forEach((cancel: Canceler) => {
        return cancel();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    handleChange,
    clickSubmit,
    clickBack,
    customer,
    canCreate,
    validatePassword,
    isMd,
    t,
    selectedStore,
  };
};
